/**
 * Urologic Instruments Educational Content
 * Images are sourced from verified medical repositories (Wikimedia Commons, NIH, etc.)
 * Each image includes source attribution for medical accuracy
 */

export const urologicInstrumentsContent = {
  id: 'urologic-instruments',
  title: {
    en: 'Urologic Instruments – A Patient\'s Guide',
    es: 'Instrumentos Urológicos – Guía para el Paciente'
  },
  subtitle: {
    en: 'Understanding the tools your urologist uses',
    es: 'Entendiendo las herramientas que usa su urólogo'
  },
  heroImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Cystoscope-med-20050425.jpg/1200px-Cystoscope-med-20050425.jpg',
  heroImageSource: 'Wikimedia Commons',
  instruments: [
    {
      id: 'cystoscope',
      name: {
        en: 'Cystoscope',
        es: 'Cistoscopio'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Cystoscope-med-20050425.jpg/800px-Cystoscope-med-20050425.jpg',
      imageSource: {
        en: 'Source: Wikimedia Commons (CC BY-SA 3.0)',
        es: 'Fuente: Wikimedia Commons (CC BY-SA 3.0)'
      },
      description: {
        en: `A cystoscope is a thin, lighted tube with a camera that allows your urologist to see inside your bladder and urethra. It's the most commonly used scope in urology.

**How it's used:** The scope is gently inserted through the urethra (the tube that carries urine out of your body) and into the bladder. The camera sends images to a screen so the doctor can examine the bladder lining.

**What it can detect:**
• Bladder tumors or cancer
• Bladder stones
• Inflammation or infection
• Abnormal tissue
• Sources of bleeding

**Types:**
• **Flexible cystoscope** – Bendable, more comfortable, often done in the office with local numbing gel
• **Rigid cystoscope** – Straight, allows for more procedures, typically done in an operating room

**What to expect:** The procedure usually takes 5-15 minutes. You may feel pressure or a brief burning sensation. Most patients can drive themselves home afterward.`,
        es: `Un cistoscopio es un tubo delgado e iluminado con una cámara que permite a su urólogo ver el interior de su vejiga y uretra. Es el endoscopio más comúnmente utilizado en urología.

**Cómo se usa:** El endoscopio se inserta suavemente a través de la uretra y hacia la vejiga. La cámara envía imágenes a una pantalla para que el médico pueda examinar el revestimiento de la vejiga.

**Qué puede detectar:**
• Tumores o cáncer de vejiga
• Cálculos en la vejiga
• Inflamación o infección
• Tejido anormal
• Fuentes de sangrado

**Tipos:**
• **Cistoscopio flexible** – Más cómodo, a menudo se realiza en el consultorio
• **Cistoscopio rígido** – Permite más procedimientos, típicamente en quirófano`
      }
    },
    {
      id: 'ureteroscope',
      name: {
        en: 'Ureteroscope',
        es: 'Ureteroscopio'
      },
      image: 'https://customer-assets.emergentagent.com/job_med-education-4/artifacts/n19z1pkm_IMG_5015.jpeg',
      imageSource: {
        en: 'Source: Boston Scientific Corporation',
        es: 'Fuente: Boston Scientific Corporation'
      },
      description: {
        en: `A ureteroscope is a very thin, long scope designed to travel up from the bladder into the ureter (the tube connecting the kidney to the bladder) and even into the kidney itself.

**How it's used:** The scope passes through the urethra, bladder, and up into the ureter. It allows direct visualization and treatment of problems in the upper urinary tract.

**What it's used for:**
• Removing or breaking up kidney stones
• Taking biopsies of suspicious tissue
• Treating tumors in the ureter or kidney
• Diagnosing causes of blood in urine

**Types:**
• **Semi-rigid ureteroscope** – Used mainly for stones in the lower ureter
• **Flexible ureteroscope** – Can bend to reach into all areas of the kidney

**What to expect:** This procedure is typically done under anesthesia. A temporary stent is often placed afterward to help the ureter heal.`,
        es: `Un ureteroscopio es un endoscopio muy delgado y largo diseñado para viajar desde la vejiga hacia el uréter e incluso hasta el riñón.

**Cómo se usa:** El endoscopio pasa a través de la uretra, la vejiga y sube hacia el uréter. Permite la visualización directa y el tratamiento de problemas en el tracto urinario superior.

**Para qué se usa:**
• Eliminar o romper cálculos renales
• Tomar biopsias de tejido sospechoso
• Tratar tumores en el uréter o riñón
• Diagnosticar causas de sangre en la orina

**Qué esperar:** Este procedimiento generalmente se realiza bajo anestesia. A menudo se coloca un stent temporal después para ayudar a que el uréter sane.`
      }
    },
    {
      id: 'resectoscope',
      name: {
        en: 'Resectoscope',
        es: 'Resectoscopio'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Urologist_performing_a_TURP.png/800px-Urologist_performing_a_TURP.png',
      imageSource: {
        en: 'Source: Wikimedia Commons – Urologist performing TURP (CC BY-SA 4.0)',
        es: 'Fuente: Wikimedia Commons – Urólogo realizando RTUP (CC BY-SA 4.0)'
      },
      description: {
        en: `A resectoscope is a specialized scope with an electrical cutting loop at the end. It's used to remove tissue from inside the bladder or prostate.

**How it's used:** The scope is inserted through the urethra. The electrical loop can cut away tissue piece by piece while the camera provides a clear view. Fluid flows through the scope to wash away debris and keep the view clear.

**What it's used for:**
• **TURBT** (Transurethral Resection of Bladder Tumor) – Removing bladder tumors
• **TURP** (Transurethral Resection of Prostate) – Removing prostate tissue for BPH
• Removing scar tissue from the urethra or bladder

**What to expect:** These procedures are done under anesthesia in an operating room. A catheter is typically placed for 1-3 days afterward. You may see blood in your urine for a few weeks.`,
        es: `Un resectoscopio es un endoscopio especializado con un asa de corte eléctrica en el extremo. Se usa para eliminar tejido del interior de la vejiga o la próstata.

**Cómo se usa:** El endoscopio se inserta a través de la uretra. El asa eléctrica puede cortar tejido pieza por pieza mientras la cámara proporciona una vista clara.

**Para qué se usa:**
• **RTUP** (Resección Transuretral de Tumor de Vejiga) – Eliminar tumores de vejiga
• **RTUP** (Resección Transuretral de Próstata) – Eliminar tejido prostático para HPB
• Eliminar tejido cicatricial de la uretra o vejiga

**Qué esperar:** Estos procedimientos se realizan bajo anestesia en un quirófano. Generalmente se coloca un catéter por 1-3 días después.`
      }
    },
    {
      id: 'ureteral-stent',
      name: {
        en: 'Ureteral Stent',
        es: 'Stent Ureteral'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/5/53/Ureteral_stent.jpg',
      imageSource: {
        en: 'Source: Wikimedia Commons – Medical illustration (Public Domain)',
        es: 'Fuente: Wikimedia Commons – Ilustración médica (Dominio Público)'
      },
      description: {
        en: `A ureteral stent is a thin, flexible plastic tube placed inside the ureter to keep it open and allow urine to drain from the kidney to the bladder.

**How it looks:** A stent is typically 20-30 cm long with a curl at each end (called a "double-J" or "pigtail" stent). One curl sits in the kidney and the other in the bladder to hold it in place.

**When it's used:**
• After kidney stone treatment to allow swelling to subside
• To bypass a blockage in the ureter
• Before or after ureter surgery
• To relieve obstruction from tumors or scar tissue

**What to expect with a stent:**
• Frequent urination
• Urgency (feeling you need to go right away)
• Blood in urine, especially after activity
• Discomfort in the kidney area when urinating
• String may hang from the urethra for easy removal

**Removal:** Most stents are removed in 1-6 weeks. Removal takes only a few seconds using a cystoscope or by pulling the attached string.`,
        es: `Un stent ureteral es un tubo plástico delgado y flexible colocado dentro del uréter para mantenerlo abierto y permitir que la orina drene del riñón a la vejiga.

**Cómo se ve:** Un stent típicamente mide 20-30 cm de largo con un rizo en cada extremo (llamado stent "doble J"). Un rizo se asienta en el riñón y el otro en la vejiga.

**Cuándo se usa:**
• Después del tratamiento de cálculos renales
• Para evitar un bloqueo en el uréter
• Antes o después de cirugía del uréter
• Para aliviar obstrucción por tumores o tejido cicatricial

**Qué esperar con un stent:**
• Micción frecuente
• Urgencia
• Sangre en la orina, especialmente después de actividad
• Molestia en el área del riñón al orinar

**Remoción:** La mayoría de los stents se retiran en 1-6 semanas.`
      }
    },
    {
      id: 'foley-catheter',
      name: {
        en: 'Foley Catheter',
        es: 'Catéter Foley'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Foley_Catheter.png/600px-Foley_Catheter.png',
      imageSource: {
        en: 'Source: Wikimedia Commons – Illustration by BruceBlaus (CC BY 3.0)',
        es: 'Fuente: Wikimedia Commons – Ilustración por BruceBlaus (CC BY 3.0)'
      },
      description: {
        en: `A Foley catheter is a flexible tube inserted through the urethra into the bladder to drain urine. It has a small balloon at the tip that is inflated with water to hold it in place.

**How it works:** The catheter is lubricated and gently inserted into the bladder. Once in place, the balloon is inflated with sterile water. Urine drains continuously into a collection bag.

**When it's used:**
• After surgery on the bladder, prostate, or urethra
• When someone cannot urinate on their own (urinary retention)
• To measure urine output accurately in the hospital
• For patients who cannot get to the bathroom

**Types:**
• **Standard Foley** – Most common, drains into a leg bag or bedside bag
• **Three-way Foley** – Has an extra channel for irrigation (flushing the bladder)

**What to expect:**
• Some discomfort or pressure when first placed
• Occasional bladder spasms (a sudden urge to urinate)
• Urine should be light yellow; notify your doctor if it becomes very bloody

**Care:** Keep the bag below your bladder level. Clean around the catheter daily. Drink plenty of fluids.`,
        es: `Un catéter Foley es un tubo flexible insertado a través de la uretra hacia la vejiga para drenar la orina. Tiene un pequeño globo en la punta que se infla con agua para mantenerlo en su lugar.

**Cómo funciona:** El catéter se lubrica y se inserta suavemente en la vejiga. Una vez en su lugar, el globo se infla con agua estéril. La orina drena continuamente hacia una bolsa de recolección.

**Cuándo se usa:**
• Después de cirugía en la vejiga, próstata o uretra
• Cuando alguien no puede orinar por sí mismo (retención urinaria)
• Para medir la producción de orina con precisión en el hospital
• Para pacientes que no pueden ir al baño

**Qué esperar:**
• Algo de incomodidad o presión cuando se coloca por primera vez
• Espasmos ocasionales de la vejiga
• La orina debe ser amarillo claro; notifique a su médico si se vuelve muy sanguinolenta

**Cuidado:** Mantenga la bolsa por debajo del nivel de su vejiga. Limpie alrededor del catéter diariamente. Beba muchos líquidos.`
      }
    },
    {
      id: 'nephrostomy-tube',
      name: {
        en: 'Nephrostomy Tube',
        es: 'Tubo de Nefrostomía'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/N01224_H_nephrostomy.jpg/600px-N01224_H_nephrostomy.jpg',
      imageSource: {
        en: 'Source: Wikimedia Commons – Medical diagram (CC BY-SA 3.0)',
        es: 'Fuente: Wikimedia Commons – Diagrama médico (CC BY-SA 3.0)'
      },
      description: {
        en: `A nephrostomy tube is a catheter placed directly through the skin and into the kidney to drain urine. It bypasses the ureter completely.

**How it's placed:** Using X-ray or ultrasound guidance, a needle is inserted through your back into the kidney. The tube is then threaded over a wire and secured to your skin. Urine drains into an external bag.

**When it's used:**
• When the ureter is completely blocked and a stent cannot be placed
• For infected kidney with blockage (emergency drainage)
• To divert urine while the ureter or bladder heals
• Before certain kidney surgeries

**What to expect:**
• A tube exits your back/side and connects to a drainage bag
• The site may be sore for a few days
• You'll need to empty the bag regularly
• Keep the site clean and dry

**Care:** Change the dressing as instructed. Watch for signs of infection (fever, redness, increasing pain). The tube is typically exchanged every 2-3 months if needed long-term.`,
        es: `Un tubo de nefrostomía es un catéter colocado directamente a través de la piel hacia el riñón para drenar la orina. Evita completamente el uréter.

**Cómo se coloca:** Usando guía de rayos X o ultrasonido, se inserta una aguja a través de su espalda hacia el riñón. Luego se enhebra el tubo y se asegura a su piel. La orina drena hacia una bolsa externa.

**Cuándo se usa:**
• Cuando el uréter está completamente bloqueado
• Para riñón infectado con bloqueo (drenaje de emergencia)
• Para desviar la orina mientras el uréter o la vejiga sana
• Antes de ciertas cirugías renales

**Qué esperar:**
• Un tubo sale de su espalda/costado y se conecta a una bolsa de drenaje
• El sitio puede estar adolorido por unos días
• Necesitará vaciar la bolsa regularmente
• Mantenga el sitio limpio y seco`
      }
    },
    {
      id: 'lithotripter',
      name: {
        en: 'Lithotripter (Shock Wave Machine)',
        es: 'Litotritor (Máquina de Ondas de Choque)'
      },
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Lithotriptor_machine.jpg/800px-Lithotriptor_machine.jpg',
      imageSource: {
        en: 'Source: Wikimedia Commons – Photo by DiverDave (CC BY-SA 3.0)',
        es: 'Fuente: Wikimedia Commons – Foto por DiverDave (CC BY-SA 3.0)'
      },
      description: {
        en: `A lithotripter is a machine that uses shock waves to break kidney stones into smaller pieces that can pass in the urine. This non-invasive treatment is called ESWL (Extracorporeal Shock Wave Lithotripsy).

**How it works:** You lie on a table or in a tub of water. The machine generates shock waves that are focused on the stone using X-ray or ultrasound guidance. The waves travel through your body and break the stone into sand-like particles.

**When it's used:**
• For kidney stones less than 2 cm
• For stones in the kidney or upper ureter
• When stones are visible on X-ray

**What to expect:**
• Treatment takes 45-60 minutes
• You may feel tapping or mild discomfort during treatment
• Sedation or light anesthesia is often used
• Stone fragments pass in urine over days to weeks
• Blood in urine is normal for a few days

**Limitations:** Not effective for very hard stones, very large stones, or stones in certain locations. Some patients need multiple treatments.`,
        es: `Un litotritor es una máquina que usa ondas de choque para romper los cálculos renales en piezas más pequeñas que pueden pasar en la orina. Este tratamiento no invasivo se llama LEOC (Litotricia Extracorpórea por Ondas de Choque).

**Cómo funciona:** Usted se acuesta en una mesa o en una tina de agua. La máquina genera ondas de choque que se enfocan en el cálculo usando guía de rayos X o ultrasonido.

**Cuándo se usa:**
• Para cálculos renales menores de 2 cm
• Para cálculos en el riñón o uréter superior
• Cuando los cálculos son visibles en rayos X

**Qué esperar:**
• El tratamiento dura 45-60 minutos
• Puede sentir golpeteo o molestia leve durante el tratamiento
• A menudo se usa sedación o anestesia ligera
• Los fragmentos de cálculos pasan en la orina durante días a semanas
• La sangre en la orina es normal por unos días`
      }
    },
    {
      id: 'laser-fiber',
      name: {
        en: 'Laser Fiber',
        es: 'Fibra Láser'
      },
      image: 'https://fairbanksurology.com/wp-content/uploads/2021/10/Screenshot2018-04-2803.29.48.png',
      imageSource: {
        en: 'Source: Fairbanks Urology (fairbanksurology.com)',
        es: 'Fuente: Fairbanks Urology (fairbanksurology.com)'
      },
      description: {
        en: `A laser fiber is a thin, flexible cable that delivers laser energy through a scope to treat various urologic conditions. Different types of lasers are used for different purposes.

**Types of lasers in urology:**

**Holmium Laser:**
• Used for breaking up kidney stones (laser lithotripsy)
• Cutting prostate tissue (HoLEP for BPH)
• Treating strictures (scar tissue)
• Removing tumors

**Thulium Laser:**
• Similar uses to holmium
• Often used for prostate surgery

**GreenLight (KTP) Laser:**
• Vaporizes prostate tissue for BPH
• Good for patients on blood thinners

**How it works:** The thin fiber passes through the working channel of a scope. When activated, it delivers precise laser energy to cut, vaporize, or break apart tissue or stones.

**Benefits:**
• Very precise
• Less bleeding than traditional cutting
• Can reach areas difficult to access surgically`,
        es: `Una fibra láser es un cable delgado y flexible que entrega energía láser a través de un endoscopio para tratar varias condiciones urológicas.

**Tipos de láser en urología:**

**Láser de Holmio:**
• Usado para romper cálculos renales
• Cortar tejido prostático (HoLEP para HPB)
• Tratar estrechamientos (tejido cicatricial)
• Eliminar tumores

**Láser de Tulio:**
• Usos similares al holmio
• A menudo usado para cirugía de próstata

**Láser GreenLight (KTP):**
• Vaporiza tejido prostático para HPB
• Bueno para pacientes con anticoagulantes

**Cómo funciona:** La fibra delgada pasa a través del canal de trabajo de un endoscopio. Cuando se activa, entrega energía láser precisa para cortar, vaporizar o romper tejido o cálculos.`
      }
    }
  ]
};
